import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  CheckCircle, 
  Shield, 
  Clock, 
  Users, 
  TrendingUp, 
  MapPin, 
  FileCheck, 
  Truck, 
  Building2,
  ArrowRight,
  Star,
  Globe
} from 'lucide-react';
import LeadCaptureModal from '../LeadCaptureModal';

const MarketingHomePage = () => {
  const [showLeadModal, setShowLeadModal] = useState(false);

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-purple-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Revolucione a Gestão de
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600"> Qualidade</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              O QualiManager conecta empresas e fornecedores em um ecossistema inteligente de gestão de conformidade, 
              documentação e marketplace B2B.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setShowLeadModal(true)}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <span>Começar Agora</span>
                <ArrowRight className="h-5 w-5" />
              </button>
              <Link
                to="/app/login"
                className="border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-lg font-semibold hover:border-blue-600 hover:text-blue-600 transition-all duration-200 inline-flex items-center justify-center"
              >
                Já sou cliente
              </Link>
            </div>
            
            {/* Modal de Captação de Lead */}
            <LeadCaptureModal 
              isOpen={showLeadModal} 
              onClose={() => setShowLeadModal(false)} 
            />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-blue-600 mb-2">500+</div>
              <div className="text-gray-600">Empresas Conectadas</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-purple-600 mb-2">2000+</div>
              <div className="text-gray-600">Fornecedores Ativos</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-green-600 mb-2">95%</div>
              <div className="text-gray-600">Redução no Tempo</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-orange-600 mb-2">24/7</div>
              <div className="text-gray-600">Monitoramento</div>
            </div>
          </div>
        </div>
      </section>

      {/* Para Empresas Section */}
      <section id="para-empresas" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Para Empresas
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Simplifique a gestão de fornecedores e garanta conformidade total com processos automatizados
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Shield className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Conformidade Garantida</h3>
                    <p className="text-gray-600">
                      Monitore automaticamente vencimentos de documentos e certificações de todos os seus fornecedores.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="bg-green-100 p-3 rounded-lg">
                    <Clock className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Agilidade nos Processos</h3>
                    <p className="text-gray-600">
                      Reduza em até 95% o tempo gasto com validação de documentos e aprovação de fornecedores.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="bg-purple-100 p-3 rounded-lg">
                    <TrendingUp className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Marketplace Inteligente</h3>
                    <p className="text-gray-600">
                      Receba propostas de fornecedores pré-validados e com documentação já analisada.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-2xl shadow-xl">
              <div className="text-center mb-6">
                <Building2 className="h-16 w-16 text-blue-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900">Dashboard Executivo</h3>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                  <span className="text-gray-700">Fornecedores Ativos</span>
                  <span className="font-bold text-green-600">127</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                  <span className="text-gray-700">Docs Vencendo</span>
                  <span className="font-bold text-blue-600">3</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                  <span className="text-gray-700">Propostas Recebidas</span>
                  <span className="font-bold text-purple-600">15</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Para Fornecedores Section */}
      <section id="para-fornecedores" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Para Fornecedores
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Expanda seus negócios e mantenha-se sempre em conformidade com nosso ecossistema inteligente
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-8 rounded-2xl">
              <div className="text-center mb-6">
                <MapPin className="h-16 w-16 text-blue-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900">Mapa de Demanda</h3>
                <p className="text-gray-600 mt-2">Visualize oportunidades por região</p>
              </div>
              
              {/* Mapa representativo */}
              <div className="bg-white p-6 rounded-xl shadow-lg">
                <div className="relative h-48 bg-gradient-to-br from-blue-100 to-green-100 rounded-lg overflow-hidden">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <Globe className="h-12 w-12 text-blue-600 mx-auto mb-2" />
                      <div className="text-sm text-gray-600">Mapa Interativo</div>
                      <div className="text-xs text-gray-500">Demanda por raio de 50km</div>
                    </div>
                  </div>
                  
                  {/* Pontos de demanda simulados */}
                  <div className="absolute top-4 left-8 w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                  <div className="absolute top-12 right-12 w-3 h-3 bg-yellow-500 rounded-full animate-pulse"></div>
                  <div className="absolute bottom-8 left-16 w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <div className="absolute bottom-16 right-8 w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
                </div>
                
                <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <span>Alta Demanda</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <span>Média Demanda</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span>Oportunidade</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <span>Novo Mercado</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <div className="bg-green-100 p-3 rounded-lg">
                    <FileCheck className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Gestão Documental Inteligente</h3>
                    <p className="text-gray-600">
                      Mantenha todos os seus documentos organizados e receba alertas automáticos de vencimento.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Truck className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Marketplace B2B</h3>
                    <p className="text-gray-600">
                      Conecte-se com empresas que precisam dos seus produtos e serviços em sua região.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="bg-purple-100 p-3 rounded-lg">
                    <Users className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Rede de Parceiros</h3>
                    <p className="text-gray-600">
                      Faça parte de uma rede confiável de fornecedores pré-validados e aumente sua credibilidade.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* ✅ NOVO: Botão de call-to-action para fornecedores */}
              <div className="text-center mt-12">
                <Link 
                  to="/cadastro-fornecedor" 
                  className="inline-flex items-center bg-gradient-to-r from-blue-700 to-cyan-500 text-white px-8 py-4 rounded-lg font-semibold hover:from-blue-800 hover:to-cyan-600 transition-all duration-200 space-x-2"
                >
                  <span>Enviar Cadastro de Fornecedor</span>
                  <ArrowRight className="h-5 w-5" />
                </Link>
                <p className="text-gray-500 mt-3 text-sm">
                  Cadastro gratuito • Aprovação em até 48h • Suporte completo
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marketplace Section */}
      <section id="marketplace" className="py-20 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Marketplace Inteligente
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Conectamos empresas e fornecedores de forma inteligente, baseada em localização, conformidade e qualidade
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Para Empresas */}
            <div className="bg-white p-8 rounded-2xl shadow-xl">
              <div className="text-center mb-6">
                <div className="bg-blue-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <Building2 className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Para Empresas</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-gray-700">Fornecedores pré-validados</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-gray-700">Documentação analisada</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-gray-700">Propostas automáticas</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-gray-700">Comparação de preços</span>
                </li>
              </ul>
            </div>
            
            {/* Processo */}
            <div className="bg-gradient-to-br from-blue-600 to-purple-600 p-8 rounded-2xl text-white">
              <div className="text-center mb-6">
                <div className="bg-white/20 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <ArrowRight className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold">Como Funciona</h3>
              </div>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="bg-white/20 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">1</div>
                  <span>Empresa publica demanda</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="bg-white/20 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">2</div>
                  <span>Sistema identifica fornecedores</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="bg-white/20 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">3</div>
                  <span>Validação automática</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="bg-white/20 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">4</div>
                  <span>Propostas qualificadas</span>
                </div>
              </div>
            </div>
            
            {/* Para Fornecedores */}
            <div className="bg-white p-8 rounded-2xl shadow-xl">
              <div className="text-center mb-6">
                <div className="bg-green-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <Truck className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Para Fornecedores</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-gray-700">Oportunidades por região</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-gray-700">Análise de demanda</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-gray-700">Propostas direcionadas</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-gray-700">Rede de confiança</span>
                </li>
              </ul>
              
              {/* ✅ NOVO: Botão de cadastro para fornecedores */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <Link 
                  to="/cadastro-fornecedor" 
                  className="w-full bg-gradient-to-r from-blue-700 to-cyan-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-800 hover:to-cyan-600 transition-all duration-200 flex items-center justify-center space-x-2"
                >
                  <span>Enviar Cadastro de Fornecedor</span>
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              O que nossos clientes dizem
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-6 rounded-xl">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-700 mb-4">
                "O QualiManager revolucionou nossa gestão de fornecedores. Reduzimos 90% do tempo gasto com validações."
              </p>
              <div className="font-semibold text-gray-900">Maria Silva</div>
              <div className="text-gray-600 text-sm">Gerente de Compras - Empresa ABC</div>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-xl">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-700 mb-4">
                "Como fornecedor, consegui expandir meus negócios para 3 novas regiões através do marketplace."
              </p>
              <div className="font-semibold text-gray-900">João Santos</div>
              <div className="text-gray-600 text-sm">Diretor - Fornecedora XYZ</div>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-xl">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-700 mb-4">
                "A conformidade nunca foi tão simples. O sistema nos alerta sobre tudo automaticamente."
              </p>
              <div className="font-semibold text-gray-900">Ana Costa</div>
              <div className="text-gray-600 text-sm">Coordenadora de Qualidade - Indústria DEF</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Pronto para revolucionar sua gestão?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Junte-se a centenas de empresas e fornecedores que já transformaram seus processos com o QualiManager.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/app/login" 
              className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-200 flex items-center justify-center space-x-2"
            >
              <span>Começar Gratuitamente</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
            <button className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-all duration-200">
              Falar com Especialista
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MarketingHomePage;

